import classNames from 'classnames';
import { shuffle } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getImageAtSize } from '../../lib/utils';

import BaseGame, { GameApi } from './Base';

import styles from '../../../styles/games/memory.module.scss';

interface MemoryGameProps {
    game: MemoryGame;
    participation: Participation;
    completeFlipDelay?: number;
    flipbackDuration?: number;
    canPlay?: boolean;
    reload?: () => void;
    className?: string;
}

function MemoryGame({
    game = null,
    participation = null,
    canPlay = false,
    completeFlipDelay = 2000,
    flipbackDuration = 500,
    reload = null,
    className = null,
}: MemoryGameProps) {
    const { questions = [], defaultCard = null, cols = 3 } = game || {};
    const [{ id: questionId, choices = [] }] = questions || [];
    const { id: participationId, answers: userAnswers = [] } = participation || {};
    const [{ valid: answerValid = false, value: userAnswer = null } = {}] = userAnswers || [];

    const choicesKey = useMemo(
        () =>
            choices
                .map(({ id }) => id)
                .sort()
                .join('-'),
        [choices],
    );

    const cards = useMemo(
        () => shuffle(choices.reduce((total, choice) => [...total, choice, choice], [])),
        [choicesKey, participationId],
    );

    const [foundCards, setFoundCards] = useState<string[]>(
        userAnswer !== null ? userAnswer.split('-') : [],
    );
    const [flippedCardsIndex, setFlippedCardsIndex] = useState<number[]>([]);

    const { url: defaultCardUrl } =
        defaultCard !== null ? getImageAtSize(defaultCard, 'medium') : {};

    const onReset = useCallback(() => {
        setFoundCards([]);
        setFlippedCardsIndex([]);
    }, [userAnswer, setFoundCards, setFlippedCardsIndex]);

    const onClickCard = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, card: string, index: number) => {
            e.preventDefault();
            if (flippedCardsIndex.length < 2) {
                setFlippedCardsIndex([...flippedCardsIndex, index]);
            }
        },
        [flippedCardsIndex, setFlippedCardsIndex],
    );

    const apiRef = useRef<GameApi>(null);

    useEffect(() => {
        let timeout = null;
        const { answerQuestion } = apiRef.current;
        const isComplete = flippedCardsIndex.length === 2;
        if (isComplete) {
            const [{ id: cardAId }, { id: cardBId }] = flippedCardsIndex.map(
                (index) => cards[index],
            );
            const isValid = cardAId === cardBId;
            const newFoundCards = isValid ? [...foundCards, cardAId] : foundCards;
            if (isValid) {
                setFoundCards(newFoundCards);
                setFlippedCardsIndex([]);
                answerQuestion(questionId, newFoundCards.sort().join('-'));
            } else {
                timeout = setTimeout(() => {
                    setFlippedCardsIndex([]);
                }, flipbackDuration);
            }
        } else if (flippedCardsIndex.length > 0) {
            timeout = setTimeout(() => {
                setFlippedCardsIndex([]);
            }, completeFlipDelay);
        }
        return () => {
            if (timeout !== null) {
                clearTimeout(timeout);
            }
        };
    }, [
        flippedCardsIndex,
        choices,
        cards,
        foundCards,
        flipbackDuration,
        completeFlipDelay,
        setFoundCards,
        setFlippedCardsIndex,
    ]);

    const onTimerComplete = useCallback(() => {
        setFlippedCardsIndex([]);
    }, [setFlippedCardsIndex]);

    return (
        <BaseGame
            game={game}
            participation={participation}
            apiRef={apiRef}
            reload={reload}
            canPlay={canPlay}
            withTimer
            withFeedback
            isValid={answerValid}
            onReset={onReset}
            onTimerComplete={onTimerComplete}
            className={classNames([styles.container, className])}
        >
            <div className={styles.inner}>
                <div
                    className={styles.cards}
                    style={{
                        gridTemplateColumns: cols > 0 ? `repeat(${cols}, 1fr)` : '1fr',
                    }}
                >
                    {cards.map(({ id: choiceId, image: choiceImage }, index) => {
                        const { url: cardUrl } = getImageAtSize(choiceImage, 'medium');
                        return (
                            <button
                                key={`card-button-${choiceId}-${index + 1}`}
                                type="button"
                                className={classNames([
                                    styles.card,
                                    {
                                        [styles.flipped]:
                                            flippedCardsIndex.indexOf(index) !== -1 ||
                                            foundCards.indexOf(choiceId) !== -1,
                                    },
                                ])}
                                disabled={
                                    flippedCardsIndex.indexOf(index) !== -1 ||
                                    foundCards.indexOf(choiceId) !== -1
                                }
                                onClick={(e) => onClickCard(e, choiceId, index)}
                            >
                                <div className={styles.imageContainer}>
                                    {defaultCardUrl !== null ? (
                                        <img
                                            src={defaultCardUrl}
                                            className={classNames([styles.image, styles.front])}
                                        />
                                    ) : (
                                        <div className={styles.front} />
                                    )}
                                    <img
                                        src={cardUrl}
                                        className={classNames([styles.image, styles.back])}
                                    />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </BaseGame>
    );
}

export default MemoryGame;
